import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Table } from "antd";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { blogimg2, imagesend } from "../../components/imagepath";
import { Link } from "react-router-dom";
import { onShowSizeChange, itemRender } from "../../components/Pagination";
import Breadcrumb from "../../components/Common/Breadcrumb";
import TableHeader from "../../components/Common/TableHeader";
import DeleteModal from "../../components/Common/DeleteModal";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import "../../assets/css/UserList.css";
import { fetchAllUsers, fetchUserById } from "../../services/userService";
import { encrypt } from "../../utils/encryption";
import { BASE_URL_IMAGE } from "../../config/api"; // Base URL for profile image
import { jwtDecode } from "jwt-decode";

const UserList = () => {
  const [loggedInUserData, setLoggedInUserData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!loggedInUserData) return; // Wait until loggedInUserData is loaded

    const fetchUsers = async () => {
      try {
        setLoading(true);
        let users;
        // You can pass loggedInUserData.id if your API needs it
        if (loggedInUserData.user_role == '2') {
          // Fetch users from same department
          users = await fetchAllUsers(loggedInUserData.department_id);
        } else {
          // Fetch all users
          users = await fetchAllUsers(null);
        }
        setUsers(users);
      } catch (error) {
        console.error("Error fetching users:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [loggedInUserData]);

  useEffect(() => {
      const fetchAndSetUser = async () => {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const decoded = jwtDecode(token);
            const user = await fetchUserById(decoded.id);
            setLoggedInUserData(user);
          } catch (error) {
            console.error("Error decoding token or fetching user:", error);
            localStorage.removeItem("token");
            setLoggedInUserData(null);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      };
  
      fetchAndSetUser();
    }, []);

  const columns = [
    {
      title: "User Details",
      dataIndex: "Name",
      render: (text, record) => (
        <div className="d-flex align-items-center gap-3">
          <img
            src={
              record.user_profile_img
                ? `${BASE_URL_IMAGE}/uploads/userProfiles/${record.user_profile_img}`
                : blogimg2
            }
            alt="Profile"
            className="rounded-circle"
            style={{ width: 48, height: 48, objectFit: "cover" }}
          />
          <div>
            <strong className="user-name">{record.user_name}</strong>
            <div className="text-muted small">{record.user_email}</div>
            <div className="text-muted small">{record.user_contact}</div>
          </div>
        </div>
      ),
      width: "30%",
    },
    {
      title: "Department",
      dataIndex: "department_name",
      sorter: (a, b) =>
        (a.department_name || "").length - (b.department_name || "").length,
      render: (text) => (
        <span className="department-badge text-uppercase">{text || "N/A"}</span>
      ),
    },
    {
      title: "Roles",
      dataIndex: "user_role",
      render: (_, record) => {
        let roleLabel = "N/A";

        if (record.user_role == 1) roleLabel = "Admin";
        else if (record.user_role == 2) roleLabel = "Department Co-Ordinator";
        else if (record.user_role == null && record.user_isFaculty == 1)
          roleLabel = "Faculty";

        const badgeClass =
          roleLabel === "Admin"
            ? "bg-dark"
            : roleLabel === "Department Co-Ordinator"
            ? "bg-info"
            : roleLabel === "Faculty"
            ? "bg-primary"
            : "bg-secondary";

        return <span className={`badge ${badgeClass}`}>{roleLabel}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "user_status",
      render: (status) => (
        <span
          className={`badge ${status == 1 ? "bg-success" : "bg-secondary"}`}
        >
          {status == 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Link
            to={`/user-profile/${encodeURIComponent(encrypt(record.user_id))}`}
            className="btn btn-primary float-end"
          >
            View Profile
          </Link>
          <Link
            to={`/edit-users/${encodeURIComponent(encrypt(record.user_id))}`}
            className="btn btn-secondaryi float-end"
          >
            Edit
          </Link>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "User", path: "/list-users" },
    { label: "User List" },
  ];

  return (
    <>
      <Helmet>
        <title>User List | CRM Jnec</title>
      </Helmet>

      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={breadcrumbItems} />
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner message="Fetching users..." />
          ) : (
            <div className="row">
              <div className="col-sm-12">
                <div className="card card-table show-entire">
                  <div className="card-body">
                    <div className="page-table-header mb-2">
                      <div className="row align-items-center">
                        <div className="col">
                          <TableHeader title="User List" addlink="/add-users" />
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive user-list">
                      <Table
                        pagination={{
                          total: users.length,
                          showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                          onShowSizeChange: onShowSizeChange,
                          itemRender: itemRender,
                        }}
                        columns={columns}
                        dataSource={users}
                        rowKey={(record) => record.user_id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <DeleteModal id="delete_user" image={imagesend} />
      </div>
    </>
  );
};

export default UserList;
